import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import {
  keyBy,
  clone,
} from 'lodash';
import {
  withStyles,
  withMobileDialog,
  Button,
  CircularProgress,
  Typography,
} from '@material-ui/core';

import { grey } from '@material-ui/core/colors';
import {
  searchEvents as searchEventsAction,
  getAnalytics as getAnalyticsAction,
} from '@pingum/app-state-management/event/actions';

import { UserService } from '@pingum/api-services';

import {
  formatDateString,
  getThisMonthAtMidnight,
} from '../../../utils/dateHelper';
import { getTenantFromUrl } from '../../../utils/urlHelper';
import {
  isUserAdmin,
  isUserRestricted,
} from '../../../utils/tenantAuthenticationHelper';
import Page from '../../widgets/Page';
import ListView from './views/ListView';
import DisplayMessage from '../../widgets/DisplayMessage';
import DashboardHeader from './DashboardHeader';
import DashboardFilterHighlight from './DashboardFilterHighlight';
import DashboardFilter from './DashboardFilter';
import ContentLoaders from './ContentLoaders';
import DashboardView from './DashboardView';
import defaultViews from './DashboardViewSelector/defaultViews';
import EventPageDialog from './dialogs/EventPageDialog';
import ChangeStatusDialog from './dialogs/ChangeStatusDialog';

// Tutorials
import {
  USER_ONBOARDING,
  ADMIN_ONBOARDING,
} from '../../tutorials/tutorials';
import Tutorial from '../../tutorials';

import styles from './styles';

const pageSize = 50;

export class DashboardPageComponent extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      page: 0,
      size: pageSize,
      sortStartDateDesc: true,
      openEventModal: false,
      openInEditMode: false,
      selectedEventId: null,
      openChangeStatustModal: false,
      selectedEvent: {},
      isNextPageLoad: false,
      showAdminOnboarding: false,
    };
  }

  applyFilterToSearch = (dashboard, sort, reset) => {
    this.setState({
      isNextPageLoad: false,
      ...(
        reset
          ? {
            page: 0,
            size: pageSize,
          }
          : {}
      ),
    }, () => {
      const dashboardRequest = dashboard || {
        includeArchived: false,
        includeComplete: false,
      };

      this.props.searchEvents(
        dashboardRequest,
        this.state.page,
        this.state.size,
        this.state.sortStartDateDesc,
        reset,
      );
      const request = clone(dashboardRequest || {});
      request.includeComplete = true;
      if (!request.startDate) {
        request.startDate = getThisMonthAtMidnight();
      }
      this.props.getAnalytics(request);
    });
  }

  openEventModal = (selectedEventId, openInEditMode = false) => {
    this.setState({
      selectedEventId,
      openEventModal: true,
      openInEditMode,
    });
  }

  closeEventModal = () => {
    this.setState({
      selectedEventId: null,
      openEventModal: false,
      openInEditMode: false,
    });
  }

  openChangeStatustModal = (selectedEvent) => {
    this.setState({
      selectedEvent,
      openChangeStatustModal: true,
    });
  }

  closeChangeStatustModal = () => {
    this.setState({
      selectedEvent: {},
      openChangeStatustModal: false,
    });
  }

  nextPage = () => {
    this.setState((prevState) => ({
      isNextPageLoad: true,
      page: prevState.page + 1,
    }), () => {
      const {
        searchEvents,
        selectedDashboard,
      } = this.props;

      searchEvents(
        selectedDashboard.dashboardData,
        this.state.page,
        this.state.size,
        undefined,
        false,
      );
    });
  }

  handleAdminOnboardingClose = () => {
    this.setState({ showAdminOnboarding: false });
  }

  handleAdminOnboardingOpen = () => {
    this.setState({ showAdminOnboarding: true });
  }

  render() {
    const {
      eventsPageable,
      newEvents,
      classes,
      isFetchingEvents,
      selectedView,
      selectedDashboard,
      pollingError,
      lastUpdatedDate,
      fullScreen,
      profile,
    } = this.props;
    const {
      openEventModal,
      openInEditMode,
      selectedEventId,
      openChangeStatustModal,
      selectedEvent,
      isNextPageLoad,
      showAdminOnboarding,
    } = this.state;

    const roles = UserService.getUserRolesForTenant(
      profile,
      getTenantFromUrl(),
    );
    const userId = profile.user_id;
    const admin = isUserAdmin(roles);
    const restricted = isUserRestricted(roles);
    const views = keyBy(defaultViews, 'componentKey');

    return (
      <Page
        title="Dashboards"
        toolbar={(
          <DashboardHeader
            applyFilterToSearch={this.applyFilterToSearch}
            totalEvents={eventsPageable.totalElements}
            userId={userId}
            admin={admin}
            restricted={restricted}
          />
        )}
        showLoadingIndicator={isFetchingEvents}
      >
        {
          pollingError
            && (
              <Typography variant="caption" gutterBottom style={{ color: grey[600] }}>
                Last updated {
                  lastUpdatedDate
                    ? formatDateString(new Date(lastUpdatedDate))
                    : 'n/a'
                }

                <Button
                  color="primary"
                  className={classes.button}
                  size="small"
                  variant="outlined"
                  onClick={
                  () => {
                    this.applyFilterToSearch(
                      this.props.selectedDashboard.dashboardData,
                      null,
                      true,
                    );
                  }
                }
                >
                  Refresh Data
                </Button>
              </Typography>
            )
        }

        {
          (
            !isFetchingEvents
              || isNextPageLoad
          )
            && (
              <div
                name="dashboard-view"
                className={
                  !fullScreen
                    ? classes.viewContainer
                    : {}
                }
              >
                <DashboardView
                  events={eventsPageable.content}
                  Component={
                    selectedView.component
                      || (
                        views[selectedView.componentKey]
                          || {}
                      ).component
                  }
                  handleSelectEvent={this.openEventModal}
                  handleChangeStatusClick={this.openChangeStatustModal}
                  restricted={restricted}
                  onNextPage={this.nextPage}
                />
              </div>
            )
        }

        {
          !!isFetchingEvents
            && !isNextPageLoad
            && selectedView.key !== 'analytics'
            && (
              <div
                className={
                  !fullScreen
                    ? classes.viewContainer
                    : {}
                }
              >
                <ContentLoaders view={selectedView.key} />
              </div>
            )
        }

        <DashboardFilterHighlight fullScreen={fullScreen} />
        <DashboardFilter
          applyFilterToSearch={this.applyFilterToSearch}
          fullScreen={fullScreen}
        />

        {
          !isFetchingEvents
            && eventsPageable.content.length === 0
            && newEvents.length === 0
            && (
              <DisplayMessage
                title="No Events"
                subTitle="The selected dashboard and filters have no events"
                icon="check"
                color="#0091EB"
                iconColor="white"
              />
            )
        }
        {
          !eventsPageable.last
            && selectedDashboard.dashboardData
            && selectedView.key !== 'analytics'
            && (
              <div style={{ textAlign: 'center' }}>
                <div className={classes.nextPageButtonContainer}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={this.nextPage}
                    disabled={isFetchingEvents}
                  >
                    Load More
                    {
                      isFetchingEvents
                        && (
                          <CircularProgress
                            size={24}
                            className={classes.nextPageProgress}
                          />
                        )
                    }
                  </Button>
                </div>
              </div>
            )
        }

        <ChangeStatusDialog
          open={openChangeStatustModal}
          handleClose={this.closeChangeStatustModal}
          selectedEvent={selectedEvent}
        />

        <EventPageDialog
          open={openEventModal}
          openInEditMode={openInEditMode}
          handleClose={this.closeEventModal}
          eventId={selectedEventId}
        />

        <Tutorial
          tutorial={USER_ONBOARDING}
          version="v2"
          openTutorial
          handleClose={this.handleUserOnboardingClose}
          handleOpen={this.handleUserOnboardingOpen}
        />
        {
          admin && (
            <Tutorial
              tutorial={ADMIN_ONBOARDING}
              version="v1"
              openTutorial={showAdminOnboarding}
              handleOpen={this.handleAdminOnboardingOpen}
              handleClose={this.handleAdminOnboardingClose}
            />
          )
        }
      </Page>
    );
  }
}

DashboardPageComponent.propTypes = {
  selectedDashboard: PropTypes.object,
  eventsPageable: PropTypes.object,
  newEvents: PropTypes.array,
  classes: PropTypes.object,
  isFetchingEvents: PropTypes.bool,
  selectedView: PropTypes.object,
  searchEvents: PropTypes.func,
  pollingError: PropTypes.bool,
  lastUpdatedDate: PropTypes.object,
  fullScreen: PropTypes.bool,
  getAnalytics: PropTypes.func,
  profile: PropTypes.object,
};

DashboardPageComponent.defaultProps = {
  selectedDashboard: {},
  eventsPageable: {
    content: [],
    last: true,
    totalElements: 0,
  },
  newEvents: [],
  classes: {},
  isFetchingEvents: true,
  selectedView: {
    name: 'List view',
    component: ListView,
  },
  searchEvents: () => {},
  pollingError: false,
  lastUpdatedDate: null,
  fullScreen: false,
  getAnalytics: () => {},
  profile: {},
};

export const mapStateToProps = (state) => ({
  selectedDashboard: state.dashboard.currentFilterDashboard,
  eventsPageable: state.event.eventsPageable,
  newEvents: state.event.newEvents,
  isFetchingEvents: state.event.isFetchingEvents,
  selectedView: state.dashboard.selectedView,
  pollingError: state.event.pollingError,
  lastUpdatedDate: state.event.lastUpdatedDate,
  profile: state.user.user,
});

export const mapDispatchToProps = (dispatch) => ({
  searchEvents: (searchEventsRequest, page, size, sortStartDateDesc, resetEventList) => dispatch(searchEventsAction(
    searchEventsRequest,
    page,
    size,
    sortStartDateDesc,
    resetEventList,
  )),
  getAnalytics: (request) => dispatch(getAnalyticsAction(request)),
});

export const DashboardPageConnected = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withMobileDialog({ breakpoint: 'xs' })(withStyles(styles)(DashboardPageComponent)));
export default withRouter(DashboardPageConnected);
